import React, { useState, useCallback } from 'react';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { Box, Typography, Chip } from '@mui/material';
import { Folder as FolderIcon, FolderOpen, Home } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { filesAPI } from '../../services/api';

function FolderNode({ node, onFolderSelect, onContextMenu, onFileDrop }) {
  const qc = useQueryClient();
  const [dragOver, setDragOver] = useState(false);

  const handleMouseEnter = () => {
    qc.prefetchQuery({
      queryKey: ['files', { folderId: node.id, page: 1, search: '', ordering: '-created_at' }],
      queryFn: () => filesAPI.list({ folderId: node.id }),
      staleTime: 30_000,
    });
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onContextMenu) onContextMenu(e, node);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.types.includes('application/x-file-id')) {
      e.dataTransfer.dropEffect = 'move';
      setDragOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const fileId = e.dataTransfer.getData('application/x-file-id');
    if (fileId && onFileDrop) {
      onFileDrop(fileId, node.id);
    }
  };

  return (
    <TreeItem
      itemId={node.id}
      label={
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: 0.3,
            px: 0.5,
            borderRadius: 1,
            bgcolor: dragOver ? 'primary.50' : 'transparent',
            border: dragOver ? '2px dashed' : '2px solid transparent',
            borderColor: dragOver ? 'primary.main' : 'transparent',
            transition: 'all 0.15s',
          }}
          onMouseEnter={handleMouseEnter}
          onContextMenu={handleContextMenu}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={(e) => {
            e.stopPropagation();
            onFolderSelect(node.id);
          }}
        >
          {dragOver ? (
            <FolderOpen fontSize="small" color="primary" />
          ) : (
            <FolderIcon fontSize="small" color="action" />
          )}
          <Typography variant="body2" sx={{ flex: 1 }} noWrap>
            {node.name}
          </Typography>
          {(node.total_files_count || node.files_count) > 0 && (
            <Chip label={node.total_files_count || node.files_count} size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
          )}
        </Box>
      }
    >
      {node.subfolders?.map((child) => (
        <FolderNode key={child.id} node={child} onFolderSelect={onFolderSelect} onContextMenu={onContextMenu} onFileDrop={onFileDrop} />
      ))}
    </TreeItem>
  );
}

export default function FolderTree({ tree, selectedFolderId, onFolderSelect, expandedItems, onExpandedItemsChange, onContextMenu, onFileDrop }) {
  const [rootDragOver, setRootDragOver] = useState(false);

  if (!tree) return null;

  const handleRootContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onContextMenu) onContextMenu(e, null);
  };

  const handleRootDragOver = (e) => {
    e.preventDefault();
    if (e.dataTransfer.types.includes('application/x-file-id')) {
      e.dataTransfer.dropEffect = 'move';
      setRootDragOver(true);
    }
  };

  const handleRootDragLeave = () => setRootDragOver(false);

  const handleRootDrop = (e) => {
    e.preventDefault();
    setRootDragOver(false);
    const fileId = e.dataTransfer.getData('application/x-file-id');
    if (fileId && onFileDrop) {
      onFileDrop(fileId, null);
    }
  };

  return (
    <SimpleTreeView
      selectedItems={selectedFolderId || '__root__'}
      expandedItems={expandedItems}
      onExpandedItemsChange={(_, ids) => onExpandedItemsChange(ids)}
      sx={{
        '& .MuiTreeItem-content': {
          borderRadius: 1,
          py: 0.2,
        },
      }}
    >
      {/* Root item */}
      <TreeItem
        itemId="__root__"
        label={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: 0.3,
              px: 0.5,
              borderRadius: 1,
              bgcolor: rootDragOver ? 'primary.50' : 'transparent',
              border: rootDragOver ? '2px dashed' : '2px solid transparent',
              borderColor: rootDragOver ? 'primary.main' : 'transparent',
              transition: 'all 0.15s',
            }}
            onContextMenu={handleRootContextMenu}
            onDragOver={handleRootDragOver}
            onDragLeave={handleRootDragLeave}
            onDrop={handleRootDrop}
            onClick={(e) => {
              e.stopPropagation();
              onFolderSelect(null);
            }}
          >
            <Home fontSize="small" color={rootDragOver ? 'primary' : 'primary'} />
            <Typography variant="body2" fontWeight={500}>
              Root
            </Typography>
          </Box>
        }
      />

      {tree.map((node) => (
        <FolderNode key={node.id} node={node} onFolderSelect={onFolderSelect} onContextMenu={onContextMenu} onFileDrop={onFileDrop} />
      ))}
    </SimpleTreeView>
  );
}
